import { ReactElement } from 'react';
import { Tooltip, Typography } from '@mui/material';
import { smallCurrencyFormat } from 'common/formats/formats';
import { GridType, Row } from 'common/types/scalarSpreadsheet';
import ClosedTransactionsCell from 'pages/Intelligence/DailyNav/components/ClosedTransactionsCell';
import DeltaCell from 'pages/Intelligence/DailyNav/components/DeltaCell';
import ReviewPriceCell from 'pages/Intelligence/DailyNav/components/ReviewPriceCell';
import {
  buildDailyNavSections,
  DAILY_NAV_COLUMN_HEADER_TOOLTIPS,
  DAILY_NAV_COLUMN_TYPES,
  DAILY_NAV_SPREADSHEET_SECTIONS_ALIAS,
  DAILY_NAV_SPREADSHEET_TITLE_ALIAS,
  DAILY_NAV_TOOLTIP_SLOT_PROPS,
  getDailyNavColumnLabel,
  getDailyNavColumnLabels,
  parseSecondaryPriceProviderCode,
} from 'pages/Intelligence/DailyNav/daily-nav.constants';
import { getExpression, getObjectValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import NewsCell from '../../../../components/NewsCell';
import { DailyNavColumn, DailyNavTableRow } from '../../useDailyNavSheet.types';

// Straight from the server's own `exceeded` -- never recomputed here, since which
// threshold applied is a server-side resolution.
const isDeltaExceeded = (row: DailyNavTableRow, columnType: string): boolean => {
  const { breakdown, secondary_breakdown: secondaryBreakdown } = row.record;
  switch (columnType) {
    case DAILY_NAV_COLUMN_TYPES.SHORT_WINDOW_DELTA:
      return Boolean(breakdown?.short_window?.exceeded);
    case DAILY_NAV_COLUMN_TYPES.LONG_WINDOW_DELTA:
      return Boolean(breakdown?.long_window?.exceeded);
    case DAILY_NAV_COLUMN_TYPES.SINCE_LAST_VALUATION_DELTA:
      return Boolean(breakdown?.since_last_valuation?.exceeded);
    case DAILY_NAV_COLUMN_TYPES.SECONDARY_SHORT_WINDOW_DELTA:
      return Boolean(secondaryBreakdown?.short_window?.exceeded);
    case DAILY_NAV_COLUMN_TYPES.SECONDARY_LONG_WINDOW_DELTA:
      return Boolean(secondaryBreakdown?.long_window?.exceeded);
    case DAILY_NAV_COLUMN_TYPES.SECONDARY_SINCE_LAST_VALUATION_DELTA:
      return Boolean(secondaryBreakdown?.since_last_valuation?.exceeded);
    case DAILY_NAV_COLUMN_TYPES.SECONDARY_CHANGE_FROM_MARK:
      return Boolean(secondaryBreakdown?.change_from_mark?.exceeded);
    default:
      return false;
  }
};

// Stable per-column test ids for the three secondary cells that share one
// DeltaCell branch. A map rather than a ternary: with three cases a conditional
// silently gives two of them the same id.
const SECONDARY_DELTA_ID_SUFFIXES: Record<string, string> = {
  [DAILY_NAV_COLUMN_TYPES.SECONDARY_SHORT_WINDOW_DELTA]: 'secondary-short-window',
  [DAILY_NAV_COLUMN_TYPES.SECONDARY_LONG_WINDOW_DELTA]: 'secondary-long-window',
  [DAILY_NAV_COLUMN_TYPES.SECONDARY_SINCE_LAST_VALUATION_DELTA]: 'secondary-since-last-valuation',
  [DAILY_NAV_COLUMN_TYPES.SECONDARY_CHANGE_FROM_MARK]: 'secondary-change-from-mark',
};

// The metric that produced the deltas, as named by the server. Every row in a
// request shares one metric, so the first breakdown that carries a name wins;
// "Value" keeps the header honest when the catalog was unavailable.
const getMetricLabel = (dailyNavRows: DailyNavTableRow[]) =>
  dailyNavRows.find(row => row.record.breakdown?.metric?.display_name)?.record.breakdown?.metric
    ?.display_name ?? 'Value';

// First-row-wins, like getMetricLabel: every row shares the firm's counts.
const getWindowSessionCounts = (dailyNavRows: DailyNavTableRow[]) => {
  const row = dailyNavRows.find(
    candidate =>
      candidate.record.breakdown?.short_window_sessions != null &&
      candidate.record.breakdown?.long_window_sessions != null
  );
  return {
    shortWindowSessions: row?.record.breakdown?.short_window_sessions,
    longWindowSessions: row?.record.breakdown?.long_window_sessions,
  };
};

// Map column index -> section band cell config (label + span at each section start).
// Sections are (re)built per render because the Secondary Data section's member
// list depends on which per-provider price columns are present this time.
const getSectionBandByColumnIndex = (columnTypeOrder: string[], metricLabel: string) => {
  const bandByIndex = new Map<number, { label: string; colSpan: number }>();
  buildDailyNavSections(columnTypeOrder).forEach(section => {
    const memberIndexes = section.columnTypes
      .map(columnType => columnTypeOrder.indexOf(columnType))
      .filter(index => index >= 0)
      .sort((a, b) => a - b);
    if (memberIndexes.length) {
      const label = section.labelFromMetric ? `${section.label} ${metricLabel}` : section.label;
      bandByIndex.set(memberIndexes[0], { label, colSpan: memberIndexes.length });
    }
  });
  return bandByIndex;
};

// The column index each section starts at, minus the leftmost one: a divider on
// the first data column would double up with the grid's own edge. Derived from
// the same band map, so the rule can never drift from where the bands actually
// begin -- which matters because the provider columns shift those boundaries.
const getSectionDividerColumnIndexes = (
  sectionBandByColumnIndex: Map<number, { label: string; colSpan: number }>
): Set<number> => {
  const startIndexes = Array.from(sectionBandByColumnIndex.keys()).sort((a, b) => a - b);
  return new Set(startIndexes.filter(index => index > 0));
};

const SectionBandLabel = ({ label }: { label: string }) => (
  <Typography
    variant="caption"
    sx={{
      color: 'text.secondary',
      display: 'block',
      fontWeight: 600,
      letterSpacing: 0.75,
      textAlign: 'center',
      textTransform: 'uppercase',
    }}>
    {label}
  </Typography>
);

const ColumnHeaderWithTooltip = ({ label, tooltip, columnType }: { label: string; tooltip: string; columnType: string }) => (
  <Tooltip
    arrow
    describeChild
    placement="top"
    slotProps={DAILY_NAV_TOOLTIP_SLOT_PROPS}
    // Regular weight: the tooltip is explanatory text, not a header.
    title={
      <Typography variant="caption" sx={{ fontWeight: 400 }}>
        {tooltip}
      </Typography>
    }>
    {/* text-cell gives it the same type as every other column header. */}
    <span
      className="text-cell"
      data-testid={`daily-nav-column-header-${columnType}`}
      style={{ borderBottom: '1px dashed', cursor: 'help' }}>
      {label}
    </span>
  </Tooltip>
);

interface CellParserParams {
  column: DailyNavColumn;
  columnType: string;
  colIndex: number;
  key: string;
  row: Row<object>;
  rowByAlias: Record<string, DailyNavTableRow>;
  sectionBandByColumnIndex: Map<number, { label: string; colSpan: number }>;
  sectionDividerColumnIndexes: Set<number>;
  columnLabels: Record<string, string>;
  onReviewClick: (row: DailyNavTableRow) => void;
  canReview: boolean;
  onBreakdownClick: (row: DailyNavTableRow) => void;
  onSecondaryBreakdownClick: (row: DailyNavTableRow) => void;
  onClosedTransactionsClick: (row: DailyNavTableRow) => void;
  onNewsClick: (row: DailyNavTableRow) => void;
}

interface DailyNavCell extends Row<object> {
  key: string;
  className?: string;
  format?: object | null;
  gridType?: GridType;
  component?: ReactElement | null;
  forceComponent?: boolean;
  colSpan?: number | null;
  customFormat?: { currency: { code: string } };
}

// Values are stored in each company's cap-table currency; default to USD when a row
// has no snapshot currency so the symbol never renders blank.
const DEFAULT_CURRENCY_CODE = 'USD';

const cellParser = (params: CellParserParams): DailyNavCell => {
  const {
    column,
    columnType,
    colIndex,
    key,
    row,
    rowByAlias,
    sectionBandByColumnIndex,
    sectionDividerColumnIndexes,
    columnLabels,
    onReviewClick,
    canReview,
    onBreakdownClick,
    onSecondaryBreakdownClick,
    onClosedTransactionsClick,
    onNewsClick,
  } = params;
  const { alias: rowAlias } = getObjectValue(row);

  const columnCellValue = getObjectValue(column[String(rowAlias)]).value;
  const cell = { ...row, key, value: columnCellValue } as DailyNavCell;

  // Vertical rule at each section boundary. Applied before the per-column-type
  // branches below because several of them return early, and appended to the
  // row's own className rather than replacing it.
  if (sectionDividerColumnIndexes.has(colIndex)) {
    cell.className = [cell.className, 'division-left-only'].filter(Boolean).join(' ');
  }

  if (rowAlias === DAILY_NAV_SPREADSHEET_SECTIONS_ALIAS) {
    const band = sectionBandByColumnIndex.get(colIndex);
    cell.value = '';
    if (band) {
      cell.colSpan = band.colSpan;
      cell.component = <SectionBandLabel label={band.label} />;
      cell.forceComponent = true;
    }
    return cell;
  }

  if (rowAlias === DAILY_NAV_SPREADSHEET_TITLE_ALIAS) {
    cell.value = getDailyNavColumnLabel(columnType, columnLabels);
    const tooltip = DAILY_NAV_COLUMN_HEADER_TOOLTIPS[columnType];
    if (tooltip) {
      cell.component = (
        <ColumnHeaderWithTooltip label={String(cell.value)} tooltip={tooltip} columnType={columnType} />
      );
      cell.forceComponent = true;
    }
    return cell;
  }

  // Per-row currency: each company's monetary cells render in its own cap-table
  // currency rather than a spreadsheet-wide default. FastValueViewer resolves a
  // number cell's currency from customFormat.currency (it ignores cell.currency and
  // spreads the merged currency last), so the code must be injected there.
  const dailyNavRowForCurrency = rowByAlias[String(rowAlias)];
  const isFundSecurityRow = dailyNavRowForCurrency?.kind === 'fund_security';
  const rowCurrency = dailyNavRowForCurrency?.previousValuation?.cap_table_currency || DEFAULT_CURRENCY_CODE;

  // Previous Valuation figures are restated in the NAV reporting currency, but
  // only per field and only when a rate was available — a row whose rate was
  // missing keeps its native value. So the label comes from converted_fields,
  // never from reporting_currency alone, or an unconverted number would be
  // shown under a currency it is not in.
  const applySnapshotCurrency = (field: string) => {
    const snapshot = dailyNavRowForCurrency?.previousValuation;
    const converted = snapshot?.converted_fields?.includes(field) && snapshot?.reporting_currency;
    cell.customFormat = { currency: { code: converted || rowCurrency } };
  };

  // The Secondary Data band can be a DIFFERENT currency from the Previous
  // Valuation band: these are provider-reported prices, converted by nobody, so
  // the label has to come from what the providers actually reported. Null there
  // means they disagreed and the server withheld the average, so there is no
  // single currency to name.
  const secondaryCurrency =
    dailyNavRowForCurrency?.record.secondary_breakdown?.price?.currency || rowCurrency;
  const applySecondaryCurrency = () => {
    cell.customFormat = { currency: { code: secondaryCurrency } };
  };

  // Dynamic per-provider Secondary Data price column: currency-formatted like
  // PRICE_PER_SHARE, blank (not $0.00) when this row has no value for the provider.
  const secondaryPriceProviderCode = parseSecondaryPriceProviderCode(columnType);
  if (secondaryPriceProviderCode) {
    if (isFundSecurityRow || columnCellValue == null) {
      cell.gridType = 'string';
      cell.value = '';
    } else {
      cell.format = smallCurrencyFormat;
      cell.gridType = 'number';
      applySecondaryCurrency();
    }
    return cell;
  }

  switch (columnType) {
    // Blank while the review is pending or when nothing prices the row. Labelled
    // from its own converted_fields: it can be a different allocation from the
    // band's, converted (or not) on its own.
    case DAILY_NAV_COLUMN_TYPES.TODAYS_PRICE:
      if (columnCellValue == null) {
        cell.gridType = 'string';
        cell.value = '';
      } else {
        const todaysPrice = dailyNavRowForCurrency?.record.todays_price;
        const converted =
          todaysPrice?.converted_fields?.includes('common_share_value') && todaysPrice?.reporting_currency;
        cell.format = smallCurrencyFormat;
        cell.gridType = 'number';
        cell.customFormat = {
          currency: { code: converted || todaysPrice?.cap_table_currency || rowCurrency },
        };
      }
      break;

    // Same treatment as the per-provider price columns it averages, and the same
    // currency: whatever the providers reported. Nothing converts it, so it is
    // NOT necessarily the reporting currency, and NOT the cap-table one
    // PRICE_PER_SHARE uses -- see secondaryCurrency above.
    case DAILY_NAV_COLUMN_TYPES.SECONDARY_AVERAGE_PRICE:
      if (columnCellValue == null) {
        cell.gridType = 'string';
        cell.value = '';
      } else {
        cell.format = smallCurrencyFormat;
        cell.gridType = 'number';
        applySecondaryCurrency();
      }
      break;

    // Company row: first common, converted with the band. Fund-security
    // children: that holding's weighted share value, same currency.
    case DAILY_NAV_COLUMN_TYPES.PRICE_PER_SHARE:
      if (columnCellValue == null) {
        cell.gridType = 'string';
        cell.value = '';
      } else {
        cell.format = smallCurrencyFormat;
        cell.gridType = 'number';
        applySnapshotCurrency('common_share_value');
      }
      break;

    case DAILY_NAV_COLUMN_TYPES.SECONDARY_CHANGE_FROM_MARK:
    case DAILY_NAV_COLUMN_TYPES.SECONDARY_SHORT_WINDOW_DELTA:
    case DAILY_NAV_COLUMN_TYPES.SECONDARY_LONG_WINDOW_DELTA:
    case DAILY_NAV_COLUMN_TYPES.SECONDARY_SINCE_LAST_VALUATION_DELTA: {
      // Clickable, opening the per-provider breakdown. Reuses DeltaCell with a
      // DIFFERENT handler: the component is presentation-only, so the dialog it
      // opens is entirely the caller's choice.
      cell.gridType = 'string';
      const dailyNavRow = rowByAlias[String(rowAlias)];
      if (dailyNavRow && dailyNavRow.kind !== 'fund_security') {
        cell.component = (
          <DeltaCell
            row={dailyNavRow}
            delta={typeof columnCellValue === 'number' ? columnCellValue : null}
            onBreakdownClick={onSecondaryBreakdownClick}
            idSuffix={SECONDARY_DELTA_ID_SUFFIXES[columnType]}
            exceeded={isDeltaExceeded(dailyNavRow, columnType)}
          />
        );
        cell.forceComponent = true;
      } else {
        cell.value = '';
      }
      break;
    }

    case DAILY_NAV_COLUMN_TYPES.SHORT_WINDOW_DELTA:
    case DAILY_NAV_COLUMN_TYPES.LONG_WINDOW_DELTA:
    case DAILY_NAV_COLUMN_TYPES.SINCE_LAST_VALUATION_DELTA: {
      // Delta cells are clickable — they open the read-only breakdown dialog — so they
      // always render the DeltaCell component rather than a plain formatted value.
      cell.gridType = 'string';
      const dailyNavRow = rowByAlias[String(rowAlias)];
      if (dailyNavRow && dailyNavRow.kind !== 'fund_security') {
        cell.component = (
          <DeltaCell
            row={dailyNavRow}
            delta={typeof columnCellValue === 'number' ? columnCellValue : null}
            onBreakdownClick={onBreakdownClick}
            idSuffix={columnType}
            exceeded={isDeltaExceeded(dailyNavRow, columnType)}
          />
        );
        cell.forceComponent = true;
      } else {
        cell.value = '';
      }
      break;
    }

    case DAILY_NAV_COLUMN_TYPES.CLOSED_TRANSACTIONS: {
      cell.gridType = 'string';
      const dailyNavRow = rowByAlias[String(rowAlias)];
      const groups = dailyNavRow?.record.secondary_breakdown?.closed_transactions ?? [];
      if (dailyNavRow && dailyNavRow.kind !== 'fund_security' && groups.length > 0) {
        cell.component = (
          <ClosedTransactionsCell row={dailyNavRow} onClosedTransactionsClick={onClosedTransactionsClick} />
        );
        cell.forceComponent = true;
      } else {
        cell.value = '';
      }
      break;
    }

    case DAILY_NAV_COLUMN_TYPES.NEWS: {
      cell.gridType = 'string';
      const dailyNavRow = rowByAlias[String(rowAlias)];
      const groups = dailyNavRow?.record.secondary_breakdown?.news ?? [];
      if (dailyNavRow && dailyNavRow.kind !== 'fund_security' && groups.length > 0) {
        cell.component = (
          <NewsCell row={dailyNavRow} onNewsClick={onNewsClick} />
        );
        cell.forceComponent = true;
      } else {
        cell.value = '';
      }
      break;
    }

    case DAILY_NAV_COLUMN_TYPES.STATUS: {
      const dailyNavRow = rowByAlias[String(rowAlias)];
      cell.gridType = 'string';
      if (dailyNavRow && dailyNavRow.kind !== 'fund_security') {
        cell.component = <ReviewPriceCell row={dailyNavRow} onReviewClick={onReviewClick} canReview={canReview} />;
        cell.forceComponent = true;
      } else {
        cell.value = '';
      }
      break;
    }

    default:
      cell.gridType = 'string';
      break;
  }

  return cell;
};

interface CreateParserParams {
  columns: DailyNavColumn[];
  rowConfig: Row<object>[];
  dailyNavRows: DailyNavTableRow[];
  onReviewClick: (row: DailyNavTableRow) => void;
  columnTypeOrder: string[];
  canReview: boolean;
  onBreakdownClick: (row: DailyNavTableRow) => void;
  onSecondaryBreakdownClick: (row: DailyNavTableRow) => void;
  onClosedTransactionsClick: (row: DailyNavTableRow) => void;
  onNewsClick: (row: DailyNavTableRow) => void;
}

const createParser = (params: CreateParserParams): Record<string, DailyNavCell> => {
  const {
    columns,
    rowConfig,
    dailyNavRows,
    onReviewClick,
    columnTypeOrder,
    canReview,
    onBreakdownClick,
    onSecondaryBreakdownClick,
    onClosedTransactionsClick,
    onNewsClick,
  } = params;

  const cells: Record<string, DailyNavCell> = {};
  const alphabet = alphabetGenerator([], columns.length);
  const sectionBandByColumnIndex = getSectionBandByColumnIndex(
    columnTypeOrder,
    getMetricLabel(dailyNavRows)
  );
  const sectionDividerColumnIndexes = getSectionDividerColumnIndexes(sectionBandByColumnIndex);

  const rowByAlias: Record<string, DailyNavTableRow> = dailyNavRows.reduce(
    (accumulator, dailyNavRow) => ({ ...accumulator, [dailyNavRow.alias]: dailyNavRow }),
    {}
  );
  const { shortWindowSessions, longWindowSessions } = getWindowSessionCounts(dailyNavRows);
  const columnLabels = getDailyNavColumnLabels(shortWindowSessions, longWindowSessions);

  rowConfig.forEach((row, rowIndex) => {
    columns.forEach((column, colIndex) => {
      const rowNumber = rowIndex + 1;
      const columnLegend = alphabet[colIndex];
      const columnType = columnTypeOrder[colIndex] ?? '';
      const key = columnLegend + rowNumber;

      const cell = cellParser({
        column,
        columnType,
        colIndex,
        key,
        row,
        rowByAlias,
        sectionBandByColumnIndex,
        sectionDividerColumnIndexes,
        columnLabels,
        onReviewClick,
        canReview,
        onBreakdownClick,
        onSecondaryBreakdownClick,
        onClosedTransactionsClick,
        onNewsClick,
      });
      cell.expr = getExpression({ expr: String(cell.expr ?? cell.value ?? ''), columnLegend });

      cells[key] = cell;
    });
  });

  return cells;
};

export default createParser;
